/**
 * The observable identity: one place that knows who the fan is and tells everybody when it changes.
 *
 * Every input is an argument — the storage, the clock-free settle calls, the listeners — so this
 * whole module is testable under `node --test`. That is the rule the whole of `resources/scripts/`
 * follows for a leaf module, and the reason `authSignal.js`, which reached for `document` and
 * `window` directly, never got a test.
 *
 * WHAT IT REPLACES, AND WHY THAT IS THE POINT
 * -------------------------------------------
 * `authSignal.js` reports a sign-in and, by its own docblock, *never* a sign-out — so a fan who
 * signs out in another tab keeps whatever is on screen until the next navigation. And nothing
 * anywhere reported an identity *change*: `guardToastIdentity()` came closest, and it only runs once
 * the toast gate chain settles `armed`, which two of that chain's three subscribers never reach.
 *
 * Both gaps were account-switch bugs waiting to happen, and two of them happened — the unscoped
 * toast enrolment verdict (2026-09-03) and the prefetch cache the consent gate made unreachable
 * (D-075). This store exists so there is exactly one signal to hang a reset off, and so that signal
 * cannot be missed.
 *
 * THE PERSISTED OWNER IS THE LOAD-BEARING PART
 * --------------------------------------------
 * An identity change is not only an in-document transition. `sessionStorage` outlives a navigation,
 * so a tab can load a fresh document already holding the *previous* fan's toast watermark, snapshot
 * and consent answer — established before they signed out and back in as somebody else, possibly in
 * another tab entirely. Comparing against a persisted owner rather than against the last value this
 * page happened to see is what makes the first settle of a new document a change when it should be.
 *
 * That also means this replaces `urc.loyalty.toastFan`: consumers no longer keep their own record of
 * whose data they are holding.
 */

import { NO_FAN, fanKey, sameFan } from './state.js';

/** Where the owning fan is remembered. Deliberately per-tab, like everything it protects. */
export const OWNER_KEY = 'urc.auth.owner';

/**
 * A no-op storage, for a browser that refuses `sessionStorage` outright.
 *
 * Every read answers `null`, which normalises to "nobody owns this tab". A fan then looks like a
 * switch on every document — an extra reset, which is the safe direction: it costs a warm cache,
 * where a missed reset serves one fan another fan's data.
 *
 * @return {{getItem: Function, setItem: Function}} A storage-shaped object.
 */
function nullStorage() {
	return { getItem: () => null, setItem: () => undefined };
}

/**
 * Read a key, answering null for anything that throws.
 *
 * Private-browsing modes throw on *access*, not just on write, so the access itself is guarded.
 *
 * @param {Object} storage A Storage-like object.
 * @param {string} key     The key.
 * @return {string|null} The value, or null.
 */
function read( storage, key ) {
	try {
		return storage.getItem( key );
	} catch ( error ) {
		return null;
	}
}

/**
 * Write a key, swallowing anything that throws.
 *
 * @param {Object} storage A Storage-like object.
 * @param {string} key     The key.
 * @param {string} value   The value.
 * @return {void}
 */
function write( storage, key, value ) {
	try {
		storage.setItem( key, value );
	} catch ( error ) {
		// Nothing to do. The consequence is that the next document fires an identity change it did
		// not need to, which is the direction this whole module errs in.
	}
}

/**
 * Build an identity store.
 *
 * @param {Object} [options]         Inputs.
 * @param {Object} [options.storage] A Storage-like object. Defaults to a null storage.
 * @param {string} [options.key]     Where to remember the owning fan.
 * @return {Object} The store.
 */
export function createAuthStore( { storage = nullStorage(), key = OWNER_KEY } = {} ) {
	let current = null;
	let currentClaims = null;
	let settled = false;
	let identityChanged = false;

	const changeListeners = new Set();
	const identityListeners = new Set();

	let resolveReady;
	const ready = new Promise( resolve => {
		resolveReady = resolve;
	} );

	/**
	 * Hand a state to a listener without letting one bad listener starve the rest.
	 *
	 * @param {Set}    listeners The listeners.
	 * @param {Object} value     What to pass.
	 * @return {void}
	 */
	const announce = ( listeners, value ) => {
		[ ...listeners ].forEach( listener => {
			try {
				listener( value );
			} catch ( error ) {
				// eslint-disable-next-line no-console
				console.warn( '[auth] a listener threw', error );
			}
		} );
	};

	/**
	 * Record a new view of the fan.
	 *
	 * Called by the composition root: once when the SDK first answers, and again on every change it
	 * reports. Passing an equal state is free — nothing is announced.
	 *
	 * @param {Object|null} next     The new AuthState, or null for "nobody".
	 * @param {Object|null} [claims] The claims it came from, for `claims()`.
	 * @return {Object} The state now in force.
	 */
	const settle = ( next, claims = null ) => {
		const state = next ? { ...next } : { ...NO_FAN };

		currentClaims = claims;
		const first = ! settled;
		const previous = current;

		// The owner comparison has to happen on the FIRST settle too, against what the tab
		// remembers rather than against `previous` — which is null at that point. That is the
		// cross-document switch this store exists to catch.
		//
		// An absent record normalises to '' — nobody — rather than to "unknown", and the two
		// readings differ in exactly one case that matters. Treating it as unknown fired a reset on
		// every anonymous first visit, which is pure waste: a visitor who has never signed in has
		// no fan-scoped data to protect. Treating it as nobody still catches the case that made
		// `guardToastIdentity()` clear unconditionally — data written before this store existed,
		// with no owner recorded — because '' differs from any real `sub`.
		const owner = first ? read( storage, key ) || '' : fanKey( previous );
		const switched = owner !== fanKey( state );

		current = state;
		settled = true;

		if ( switched ) {
			identityChanged = true;
			write( storage, key, fanKey( state ) );
		}

		if ( first ) {
			resolveReady( state );
		}

		// Identity first: a subscriber that repaints must not read a cache the reset is about to
		// drop. Both sets are announced synchronously and in this order, for that reason alone.
		if ( switched ) {
			announce( identityListeners, state );
		}

		if ( first || ! sameStateAs( previous, state ) ) {
			announce( changeListeners, state );
		}

		return state;
	};

	return {
		ready,

		/**
		 * The ID-token claims the current state was derived from, or null.
		 *
		 * Read-only and deliberately not part of `AuthState`: `state()` is the contract consumers
		 * branch on, and widening it invites a surface to start reading a claim directly instead of
		 * asking ./state.js what it means.
		 *
		 * It exists for two reasons. It is the only way to tell "this fan has no club" from "the
		 * tenant does not put `team_name` on the ID token", which are the same `clubId: null` and
		 * need different fixes -- one is a fan's setting, the other is an Auth0 Action somebody has
		 * to write. And a namespaced custom claim is the agreed end state for loyalty consent in
		 * `outlier-stats`, which would otherwise have to re-read and re-decode the token to see it.
		 *
		 * @return {Object|null} The claims.
		 */
		claims: () => ( currentClaims ? { ...currentClaims } : null ),

		/**
		 * The fan right now, or null before the first settle.
		 *
		 * Null and "signed out" are different answers and callers must not conflate them: null is
		 * "the SDK has not answered", which on a cold page is the first few hundred milliseconds.
		 *
		 * @return {Object|null} The state.
		 */
		state: () => ( current ? { ...current } : null ),

		settle,

		/**
		 * Every change, sign-out and account switch included.
		 *
		 * Fires immediately when the store has already settled, so a late subscriber — a block that
		 * mounted after the SDK answered — does not wait for the next change that may never come.
		 *
		 * @param {Function} listener Called with the AuthState.
		 * @return {Function} Unsubscribe.
		 */
		subscribe( listener ) {
			changeListeners.add( listener );

			if ( settled ) {
				try {
					listener( { ...current } );
				} catch ( error ) {
					// eslint-disable-next-line no-console
					console.warn( '[auth] a listener threw', error );
				}
			}

			return () => changeListeners.delete( listener );
		},

		/**
		 * Only when the fan changes — including one fan replacing another with no sign-out between.
		 *
		 * This is the reset signal. A club change or an email change is the same person and does not
		 * fire it.
		 *
		 * **It replays for a late subscriber** when a change was already detected in this document,
		 * because such a subscriber may be holding cached data of its own that the earlier
		 * announcement could not reach.
		 *
		 * @param {Function} listener Called with the new AuthState.
		 * @return {Function} Unsubscribe.
		 */
		onIdentityChange( listener ) {
			identityListeners.add( listener );

			if ( identityChanged && settled ) {
				try {
					listener( { ...current } );
				} catch ( error ) {
					// eslint-disable-next-line no-console
					console.warn( '[auth] a listener threw', error );
				}
			}

			return () => identityListeners.delete( listener );
		},
	};
}

/**
 * Whether two states are the same in every field a consumer can see.
 *
 * Not `sameFan()`: that compares only `sub`, on purpose, because it answers a different question.
 * A fan who picks a club has not become somebody else, but a header that draws a crest does need to
 * hear about it.
 *
 * @param {Object|null} a One state.
 * @param {Object|null} b The other.
 * @return {boolean} True when nothing observable differs.
 */
export function sameStateAs( a, b ) {
	if ( ! a || ! b ) {
		return false;
	}

	return (
		sameFan( a, b ) && a.signedIn === b.signedIn && a.email === b.email && a.clubId === b.clubId
	);
}
